import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { Layout, LayoutContent } from '@astryxdesign/core/Layout';
import { BottomNav } from '@/components/BottomNav';
import { BookingSheet } from './booking/BookingSheet';
import { AssistantSheet } from './assistant/AssistantSheet';
import { PreviewBanner } from './PreviewBanner';
import { useTenant } from '@/app/tenant-context';

interface ShellCtx {
  openBooking: (serviceId?: string) => void;
  openAssistant: (question?: string) => void;
}
const Ctx = createContext<ShellCtx | null>(null);
export const useShell = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useShell outside ClientShell');
  return v;
};

export function ClientShell() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [booking, setBooking] = useState<{ open: boolean; serviceId?: string }>({ open: false });
  const [assistant, setAssistant] = useState<{ open: boolean; question?: string }>({ open: false });

  const openBooking = useCallback((serviceId?: string) => setBooking({ open: true, serviceId }), []);
  const openAssistant = useCallback((question?: string) => setAssistant({ open: true, question }), []);
  const ctx = useMemo(() => ({ openBooking, openAssistant }), [openBooking, openAssistant]);

  return (
    <Ctx.Provider value={ctx}>
      <Layout
        height="auto"
        content={
          <LayoutContent padding={0} label={tenant.name}>
            {tenant.status === 'preview' && <PreviewBanner />}
            <main className="pb-nav">
              <Outlet />
            </main>
          </LayoutContent>
        }
      />
      <BottomNav />
      <BookingSheet
        open={booking.open || pathname === '/book'}
        initialServiceId={booking.serviceId}
        onOpenChange={(open) => {
          setBooking({ open });
          if (!open && pathname === '/book') navigate('/', { replace: true });
        }}
      />
      {tenant.assistant_enabled && (
        <AssistantSheet
          open={assistant.open}
          initialQuestion={assistant.question}
          onOpenChange={(open) => setAssistant({ open })}
          onBook={(serviceId) => {
            setAssistant({ open: false });
            openBooking(serviceId);
          }}
        />
      )}
    </Ctx.Provider>
  );
}
